/**
 * Precast Split Tool (BIM: Precast > Split > Split Element)
 *
 * Click a monolithic wall or floor slab and the tool slices it into
 * transportable precast panels per the Configuration rules (max panel width,
 * weight envelope, joint gaps, hollow-core vs solid mode). The source element
 * is consumed and replaced by the generated panel set in ONE undoable command
 * — exactly the BIM precast split workflow.
 */
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import {
    BIMElement,
    FloorElement,
    WallElement,
    PrecastFloorElement,
    PrecastWallElement,
} from '../../core/types';
import { splitWallToPanels, splitFloorToPanels, isSplitSource } from '../../utils/precastGeometry';
import { getPrecastConfig } from '../../state/usePrecastConfigStore';
import i18n from '../../i18n';

export class PrecastSplitToolClass extends BaseTool {
    id = 'precast_split';
    name = 'Precast Split Element';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => isSplitSource(el));
        if (!hit) {
            this.notify(i18n.t('tools.notify.precastSplitPickAMonolithicWall'), 'warning');
            return;
        }
        this.splitElement(hit.element);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const hit = this.findElementHitAtPoint(event, (el) => isSplitSource(el));
        if (hit) {
            // Split indicator line across the picked element at the cursor.
            const p = hit.point;
            const half = 0.4;
            this.context.appStore.setToolPreview({
                type: 'polyline',
                points: [
                    new THREE.Vector3(p.x - half, p.y - half, p.z),
                    new THREE.Vector3(p.x + half, p.y + half, p.z),
                ],
                color: '#ffaa00',
            });
            this.context.appStore.setCursor('crosshair');
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.context?.appStore.setToolPreview(null);
        }
        super.onKeyDown(event);
    }

    /** Consume the monolithic host and add the generated precast panels. */
    private splitElement(element: BIMElement): void {
        if (!this.context) return;
        const config = getPrecastConfig();
        let panels: Array<PrecastWallElement | PrecastFloorElement> = [];

        if (element.category === 'Walls') {
            panels = splitWallToPanels(element as WallElement, config);
        } else if (element.category === 'Floors') {
            panels = splitFloorToPanels(element as FloorElement, config);
        }

        if (panels.length === 0) {
            this.notify(i18n.t('tools.notify.precastSplitElementIsSmallerThan'), 'warning');
            return;
        }

        // One command: remove the source, add every panel (atomic undo).
        this.context.bimStore.replaceElements(
            `Split into ${panels.length} precast panel${panels.length > 1 ? 's' : ''}`,
            [element.id],
            panels,
        );
        // Select the produced panels so the next Precast step is ready.
        this.context.bimStore.setSelection(panels.map(p => p.id));
        this.notify(i18n.t('tools.notify.precastSplitDone', { count: panels.length, joint: config.jointGapMm }), 'success');
    }
}
